<p-confirmDialog></p-confirmDialog>
<p-card header="Items">
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-end gap-2">
      <span
        [pTooltip]="canAdd ? 'Add a product to a site' : 'You do not have permission to add'"
        tooltipPosition="bottom"
        [style.display]="'inline-block'"
      >
        <p-button
          icon="pi pi-plus"
          label="Product"
          (onClick)="openProductForm()"
          [disabled]="!products || products.length === 0 || !canAdd"
        ></p-button>
      </span>
      <span
        [pTooltip]="canAdd ? 'Add a static service to a site' : 'You do not have permission to add'"
        tooltipPosition="bottom"
        [style.display]="'inline-block'"
      >
        <p-button
          icon="pi pi-plus"
          label="Static Service"
          (onClick)="openAddStaticService()"
          [disabled]="!products || products.length === 0 || !canAdd"
        ></p-button>
      </span>
      <p-button
        icon="pi pi-refresh"
        label="Refresh"
        (onClick)="refreshData()"
        pTooltip="Refresh data"
        tooltipPosition="bottom"
      ></p-button>
      <p-button
        *ngIf="hasUnsavedChanges && hasSavedData"
        icon="pi pi-times"
        label="Discard"
        (onClick)="discardChanges()"
        pTooltip="Discard unsaved changes"
        tooltipPosition="bottom"
      ></p-button>
      <p-button
        *ngIf="hasUnsavedChanges"
        icon="pi pi-save"
        label="Save"
        (onClick)="saveProducts()"
        pTooltip="Save changes"
        tooltipPosition="bottom"
      ></p-button>
      <p-button
        icon="pi pi-download"
        label="Export"
        (onClick)="exportProducts()"
        [disabled]="!matchedProducts || matchedProducts.length === 0"
        pTooltip="Export products"
        tooltipPosition="bottom"
      ></p-button>
    </div>
  </ng-template>

  <div class="mb-3">
    <label for="global-effective-date" class="text-sm font-medium block mb-2">
      <strong>Effective Date (Optional - applies to all products):</strong>
    </label>
    <p-calendar
      id="global-effective-date"
      [(ngModel)]="globalEffectiveDate"
      (onSelect)="onGlobalEffectiveDateChange()"
      (onClear)="onGlobalEffectiveDateChange()"
      [showIcon]="true"
      dateFormat="yy-mm-dd"
      [showButtonBar]="true"
      [showTime]="false"
      [hideOnDateTimeSelect]="true"
      [appendTo]="'body'"
      [baseZIndex]="10000"
      placeholder="Select effective date (defaults to today)"
      styleClass="w-full"
      [style]="{ 'max-width': '300px' }"
    ></p-calendar>
    <small class="text-600 block mt-2">
      Date when products will take effect in the billing cycle. If not set, it will default to today. You can override this for individual products when editing. Changes apply automatically to products without individual dates.
    </small>
  </div>

  <p-table
    #dt
    [value]="matchedProducts"
    [loading]="loading"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 20, 50]"
    [globalFilterFields]="['site', 'product', 'description']"
    dataKey="id">
    <ng-template pTemplate="caption">
      <p-iconField iconPosition="left">
        <p-inputIcon>
          <i class="pi pi-search"></i>
        </p-inputIcon>
        <input
          pInputText
          type="text"
          (input)="dt.filterGlobal($any($event.target).value, 'contains')"
          placeholder="Search by site, product, or description"
          class="w-full"
        />
      </p-iconField>
    </ng-template>
    <ng-template pTemplate="header">
      <tr>
        <th pSortableColumn="site">
          Site
          <p-sortIcon field="site"></p-sortIcon>
        </th>
        <th pSortableColumn="product">
          Product
          <p-sortIcon field="product"></p-sortIcon>
        </th>
        <th pSortableColumn="description">
          Description
          <p-sortIcon field="description"></p-sortIcon>
        </th>
        <th pSortableColumn="rate" class="text-right">
          Rate
          <p-sortIcon field="rate"></p-sortIcon>
        </th>
        <th pSortableColumn="quantity" class="text-right">
          Quantity
          <p-sortIcon field="quantity"></p-sortIcon>
        </th>
        <th pSortableColumn="effectiveDate">
          Effective Date
          <p-sortIcon field="effectiveDate"></p-sortIcon>
        </th>
        <th>Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-item>
      <tr>
        <td>{{ item.site }}</td>
        <td>
          {{ item.product }}
          <p-tag
            *ngIf="item.isStatic"
            value="Static"
            severity="info"
            styleClass="ml-2 text-xs"
          ></p-tag>
        </td>
        <td>{{ item.description }}</td>
        <td class="text-right">
          <span *ngIf="editingProduct?.id !== item.id">
            {{ item.rate !== undefined ? (item.rate | currency) : '-' }}
          </span>
          <input
            *ngIf="editingProduct?.id === item.id && editingProduct"
            pInputText
            type="number"
            [(ngModel)]="editingProduct.rate"
            class="text-right"
            style="width: 100px;"
            step="0.01"
            min="0"
          />
        </td>
        <td class="text-right">
          <span *ngIf="editingProduct?.id !== item.id">
            {{ item.quantity !== undefined ? item.quantity : '-' }}
          </span>
          <input
            *ngIf="editingProduct?.id === item.id && editingProduct"
            pInputText
            type="number"
            [(ngModel)]="editingProduct.quantity"
            class="text-right"
            style="width: 80px;"
            step="1"
            min="1"
          />
        </td>
        <td>
          <span *ngIf="editingProduct?.id !== item.id">
            {{ item.effectiveDate ? (item.effectiveDate | date: 'yyyy-MM-dd') : (globalEffectiveDate ? (globalEffectiveDate | date: 'yyyy-MM-dd') : 'Today (default)') }}
          </span>
          <p-calendar
            *ngIf="editingProduct?.id === item.id && editingProduct"
            [(ngModel)]="editingProduct.effectiveDate"
            [showIcon]="true"
            dateFormat="yy-mm-dd"
            [showButtonBar]="true"
            [showTime]="false"
            placeholder="Select effective date"
            [appendTo]="'body'"
            [baseZIndex]="10000"
            [panelStyleClass]="'product-edit-calendar-panel'"
            styleClass="w-full"
            [style]="{ 'max-width': '200px' }"
          ></p-calendar>
        </td>
        <td>
          <div class="flex justify-content-end gap-1" *ngIf="editingProduct?.id !== item.id">
            <span [pTooltip]="canEdit ? 'Edit' : 'No permission to edit'" tooltipPosition="top" [style.display]="'inline-block'">
              <p-button
                [icon]="'pi pi-pencil'"
                [rounded]="true"
                [raised]="true"
                severity="info"
                size="small"
                (onClick)="editProduct(item)"
                [disabled]="!canEdit"
              ></p-button>
            </span>
            <span [pTooltip]="canDelete ? 'Disconnect' : 'No permission to delete'" tooltipPosition="top" [style.display]="'inline-block'">
              <p-button
                [icon]="'pi pi-power-off'"
                [rounded]="true"
                [raised]="true"
                severity="danger"
                size="small"
                (onClick)="removeProductById(item.id || item)"
                [disabled]="!canDelete"
              ></p-button>
            </span>
          </div>
          <div class="flex justify-content-end gap-1" *ngIf="editingProduct?.id === item.id">
            <p-button
              [icon]="'pi pi-check'"
              [rounded]="true"
              [raised]="true"
              severity="success"
              size="small"
              (onClick)="saveProduct(item)"
              pTooltip="Save"
              tooltipPosition="top"
            ></p-button>
            <p-button
              [icon]="'pi pi-times'"
              [rounded]="true"
              [raised]="true"
              severity="secondary"
              size="small"
              (onClick)="cancelEdit()"
              pTooltip="Cancel"
              tooltipPosition="top"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="7" class="text-center py-7">No items found</td>
      </tr>
    </ng-template>
  </p-table>
</p-card>
<app-client-onboarding-wizard-buttons
  [nextDisabled]="false"
  (buttonClicked)="handleCommonButtonClicked($event)"></app-client-onboarding-wizard-buttons>
